/**
 * Generates a unique background pattern for a category based on its color and ID
 * Creates a halftone/dot pattern overlay similar to the design reference
 */
export function generateCategoryBackground(
  color: string | undefined | null,
  id: number
): {
  background: string
  backgroundImage: string
} {
  // Use a default color if none is provided
  const defaultColor = "#3B82F6"
  const categoryColor = color || defaultColor

  // Use the category ID as a seed for consistent patterns
  const seed = id * 12345

  // Convert hex to RGB
  const hex = categoryColor.replace("#", "")
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Create a gradient from darker to lighter (diagonal from top-left to bottom-right)
  const darkerR = Math.max(0, r - 50)
  const darkerG = Math.max(0, g - 50)
  const darkerB = Math.max(0, b - 50)

  const lighterR = Math.min(255, r + 30)
  const lighterG = Math.min(255, g + 30)
  const lighterB = Math.min(255, b + 30)

  // Base gradient (diagonal from top-left to bottom-right)
  const baseGradient = `linear-gradient(135deg, 
    rgba(${darkerR}, ${darkerG}, ${darkerB}, 1) 0%, 
    rgba(${r}, ${g}, ${b}, 0.98) 50%, 
    rgba(${lighterR}, ${lighterG}, ${lighterB}, 0.95) 100%)`

  // Create halftone/dot pattern using radial gradients
  // Pattern is more concentrated in the center and fades towards edges
  const dotSize = 1.5 // Size of each dot
  const patternIntensity = 0.12 // Opacity of the dots (subtle)

  // Generate dots in a grid-like pattern, more concentrated in center
  // Using pseudo-random positions based on seed for variety
  const dots: string[] = []
  const centerX = 50
  const centerY = 50
  const maxDistance = 60 // Maximum distance for fade calculation

  // Create dots in a radial pattern from center
  for (let i = 0; i < 25; i++) {
    const angle = (i * 137.5 + (seed % 360)) * (Math.PI / 180) // Golden angle for distribution
    const distance = (i % 5) * 15 + 10 // Varying distances from center
    const x = centerX + Math.cos(angle) * distance
    const y = centerY + Math.sin(angle) * distance

    // Fade intensity based on distance from center
    const distanceFromCenter = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2))
    const fadeFactor = Math.max(0, 1 - distanceFromCenter / maxDistance)
    const intensity = patternIntensity * fadeFactor

    if (intensity > 0.01) {
      dots.push(
        `radial-gradient(circle at ${x}% ${y}%, rgba(255, 255, 255, ${intensity}) ${dotSize}px, transparent ${dotSize + 0.5}px)`
      )
    }
  }

  // Add some random scattered dots for texture
  for (let i = 0; i < 15; i++) {
    const x = (seed * (i + 1)) % 100
    const y = (seed * (i + 2) * 7) % 100
    const distanceFromCenter = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2))
    const fadeFactor = Math.max(0, 1 - distanceFromCenter / maxDistance)
    const intensity = patternIntensity * 0.6 * fadeFactor

    if (intensity > 0.01) {
      dots.push(
        `radial-gradient(circle at ${x}% ${y}%, rgba(255, 255, 255, ${intensity}) ${dotSize * 0.8}px, transparent ${dotSize + 0.3}px)`
      )
    }
  }

  const halftonePattern = dots.join(", ")

  return {
    background: baseGradient,
    backgroundImage: `${baseGradient}, ${halftonePattern}`,
  }
}

/**
 * Generates a CSS background style string for the category card
 */
export function getCategoryBackgroundStyle(
  color: string | undefined | null,
  id: number
): React.CSSProperties {
  const { background, backgroundImage } = generateCategoryBackground(color, id)

  return {
    background: background,
    backgroundImage: backgroundImage,
  }
}
